import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Typography, withStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
    getActiveCommunityId,
    getActiveCommunityName,
    getActiveCommunityImage,
    getActiveCommunity,
    getActiveCommunityOrgId,
} from '../../../user/selectors';
import styles from './styles';
import ProTips from '../../../common/ProTips';
import DatePickerRange from '../../../common/DatePickerRange';
import ActiveChallenges from '../../../common/ActiveChallenges';
import CreateChallengeBanner from '../../../common/CreateChallengeBanner';
import ActivateModal from '../../../common/ActivateModal';
import CommonPropTypes from '../../../common/propTypes';
import {
    getChallengesScreenActionErrors,
    getChallengesScreenActionResponse,
    getGroupChallenges,
    isChallengesScreenActionLoading,
    isGroupLoading,
} from '../../ChallengesScreen/selectors';
import {
    getHeadlinePhotoUrl,
    isChallengeLoading,
} from '../../../challenges/selectors';
import {
    challengeActivationRequested,
    challengeDeactivationRequested,
    challengeReportRequested,
    challengesRequested,
    clearChallengesScreenActionErrors,
    viewChallengeRequested,
} from '../../../challenges/actions';
import {
    BUTTONS_CELL,
    DEACTIVATE_ACTION,
    GENERATE_REPORT_ACTION,
    GROUP_ACTIVE,
    GROUP_UPCOMING,
} from '../../ChallengesScreen/constants';
import { handleButtonsCell } from '../../../utils/challengeButtons';
import SnackBarAPIResponse from '../../../common/SnackbarAlert/SnackBarAPIResponse';
import { noop } from '../../../common/utils';
import { trackEvent } from '../../../utils/analytics';
import ContentLoader from 'react-content-loader';
import Graphs from './Graphs';

import AccountTierNotificationCard from '../../../common/AccountTierNotificationCard';
import { getCurrentSubscription } from '../../../subscriptions/selectors';
import { shouldShowSubscription } from '../../../subscriptions/utils';
import { Can } from '../../../casl/ability-context';
import { ANALYTICS_FILTER_CHANGED } from '../../../constants/analyticsEvents';
import { activityDataRequested } from '../../../activities/actions';
import { ActivityCardScroller } from '../../../common/ActivityCard/ActivityCardScroller';
import { ACTIVITIES_FEED_TYPE_TRENDING } from '../../../activities/constants';
import { getActivities } from '../../../activities/selectors';

export const AdminHome = ({
    classes,
    communityId,
    isLoading,
    isLoadingActive,
    isLoadingUpcoming,
    actionResponse,
    loadFunc,
    activeChallenge,
    upcomingChallenge,
    requestDeactivation,
    requestChallengeReport,
    isActionLoading,
    actionErrors,
    clearErrors,
    requestChallenge,
    challengeCoverPhotoUrl,
    accountPlanData,
    activities,
    fetchCommunityActivities,
    communityName,
    communityImage,
    community,
    activeCommunityOrgId,
}) => {
    useEffect(() => {
        if (communityId) {
            fetchCommunityActivities(communityId);
        }
    }, [communityId, fetchCommunityActivities]);
    const { t } = useTranslation();
    const history = useHistory();
    const [modalDetails, setModalDetails] = useState(null);
    const [isActionSent, setActionSent] = useState(false);
    const [tip, setTip] = useState('');
    const [haveRequestedChallenges, setHaveRequestedChallenges] = useState(
        false,
    );

    const loadMoreActivities = useCallback(
        (options) => {
            fetchCommunityActivities(communityId, options);
        },
        [fetchCommunityActivities, communityId],
    );

    const [startDate, setStartDate] = useState(moment().subtract(1, 'months'));
    const [endDate, setEndDate] = useState(moment());

    // eslint-disable-next-line react/prop-types
    const filteredActivities = activities.filter((activity) => {
        const activityDate = moment(activity.activityCreated);
        return activityDate.isBetween(startDate, endDate, 'day', '[]');
    });

    const isOrganization = activeCommunityOrgId === communityId;
    const entityType = isOrganization ? 'Organization' : 'Community';

    const handleDateChange = (values) => {
        trackEvent(ANALYTICS_FILTER_CHANGED);
        setStartDate(values.startDate);
        setEndDate(values.endDate);
    };

    useEffect(() => {
        const proTips = t('proTips.tips', { returnObjects: true });
        const randomProTip =
            proTips[Math.floor(Math.random() * proTips.length)];
        setTip(randomProTip.tip);
    }, [t]);

    const loadActiveChallenges = useCallback(() => {
        loadFunc({
            communityId,
            group: GROUP_ACTIVE,
        });
    }, [loadFunc, communityId]);

    const loadUpcomingChallenges = useCallback(() => {
        loadFunc({
            communityId,
            group: GROUP_UPCOMING,
        });
    }, [loadFunc, communityId]);

    useEffect(() => {
        loadActiveChallenges();
        loadUpcomingChallenges();
    }, [loadActiveChallenges, loadUpcomingChallenges]);

    useEffect(() => {
        if (activeChallenge?.[0]) {
            const challenge = activeChallenge[0];
            requestChallenge({ challengeId: challenge.id, communityId });
        }
        if (upcomingChallenge?.[upcomingChallenge.length - 1]) {
            const challenge = upcomingChallenge[upcomingChallenge.length - 1];
            requestChallenge({ challengeId: challenge.id, communityId });
        }
        setHaveRequestedChallenges(true);
    }, [activeChallenge, upcomingChallenge, requestChallenge, communityId]);

    useEffect(() => {
        if (
            isActionSent &&
            !isActionLoading &&
            actionErrors == null &&
            actionResponse != null
        ) {
            loadActiveChallenges();
            setActionSent(false);
        }
    }, [
        isActionSent,
        isActionLoading,
        actionErrors,
        actionResponse,
        loadActiveChallenges,
    ]);

    const handleCloseModal = () => {
        modalDetails && setModalDetails({ ...modalDetails, isOpen: false });
    };

    const handleConfirm = () => {
        handleCloseModal();
        const challengeId = modalDetails.items[0].identifier;
        const requestBody = {
            communityId,
            challengeId,
        };
        switch (modalDetails.action) {
            case DEACTIVATE_ACTION:
                requestDeactivation(requestBody);
                setActionSent(true);
                break;
            case GENERATE_REPORT_ACTION:
                requestChallengeReport(challengeId);
                break;
            default:
                break;
        }
    };

    const onButtonsCell = (id, key) => {
        const challenge = activeChallenge.find((item) => item.id === id);
        handleButtonsCell(
            id,
            key,
            challenge?.name,
            challenge?.icon?.[0]?.url,
            history,
            setModalDetails,
            t,
        );
    };

    return (
        <Grid container className={classes.container}>
            {modalDetails && (
                <ActivateModal
                    {...modalDetails}
                    onClose={handleCloseModal}
                    onCancel={handleCloseModal}
                    onConfirm={handleConfirm}
                />
            )}
            <Can I="view" a="BillingInfo">
                {shouldShowSubscription() && (
                    <AccountTierNotificationCard
                        accountPlanData={accountPlanData}
                    />
                )}
            </Can>
            <Grid
                item
                container
                direction="column"
                justify="center"
                className={classes.top}
            >
                <Typography variant="h6" className={classes.title}>
                    {`${t('adminDash')} — ${communityName}`}
                </Typography>
                <Typography className={classes.text}>
                    {t('welcomeManage', { entityType })}
                </Typography>
            </Grid>
            {isLoading ||
            isLoadingActive ||
            isLoadingUpcoming ||
            !haveRequestedChallenges ? (
                <Grid
                    container
                    direction="column"
                    className={classes.activeChallengeWrapper}
                >
                    <ContentLoader
                        speed={2}
                        backgroundColor="#f3f3f3"
                        foregroundColor="#ecebeb"
                        className={classes.contentLoaderContainer}
                    >
                        <rect
                            x="0"
                            y="0"
                            rx="2"
                            ry="2"
                            width="22%"
                            height="28"
                        />
                        <rect
                            x="0"
                            y="60"
                            rx="2"
                            ry="2"
                            width="100%"
                            height="360"
                        />
                    </ContentLoader>
                </Grid>
            ) : challengeCoverPhotoUrl !== null && activeChallenge?.[0] ? (
                <div className={classes.activeChallengeWrapper}>
                    <ActiveChallenges
                        activityTitle={t('activeChallenges.activeChallenge')}
                        defaultCoverImageUrl={challengeCoverPhotoUrl}
                        buttonsCell={BUTTONS_CELL[GROUP_ACTIVE]}
                        onButtonsCell={onButtonsCell}
                        activityItem={activeChallenge?.[0] || {}}
                        isLoading={isLoadingActive}
                    />
                </div>
            ) : challengeCoverPhotoUrl !== null && upcomingChallenge?.[0] ? (
                <div className={classes.activeChallengeWrapper}>
                    <ActiveChallenges
                        activityTitle={t(
                            'upcomingChallenges.upcomingChallenge',
                        )}
                        defaultCoverImageUrl={challengeCoverPhotoUrl}
                        buttonsCell={BUTTONS_CELL[GROUP_UPCOMING]}
                        onButtonsCell={onButtonsCell}
                        activityItem={
                            upcomingChallenge?.[upcomingChallenge.length - 1] ||
                            {}
                        }
                        isLoading={isLoadingUpcoming}
                    />
                </div>
            ) : (
                <div className={classes.activeChallengeWrapper}>
                    <CreateChallengeBanner
                        communityImage={communityImage}
                        isOrg={isOrganization}
                    />
                </div>
            )}

            <Grid item container className={classes.proTipsSection}>
                <ProTips tip={tip} />
            </Grid>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    direction="row"
                    className={classes.analyticsDatePickerContainer}
                    container
                >
                    <Grid container alignItems="center" xs={12} sm={6}>
                        <Typography className={classes.header} variant="h6">
                            {t('impact.homeHeader')}
                        </Typography>
                    </Grid>
                    <Grid container xs={12} sm={6} justify="flex-end">
                        <DatePickerRange
                            startDate={startDate}
                            endDate={endDate}
                            handleDateChange={handleDateChange}
                        />
                    </Grid>
                </Grid>
                <Graphs startDate={startDate} endDate={endDate} />
            </Grid>

            <Grid item container className={classes.photoSection}>
                <Grid item xs={12}>
                    <Typography
                        align="center"
                        variant="h5"
                        className={classes.photoSectionTitle}
                    >
                        {t('Activity')}
                    </Typography>
                </Grid>
            </Grid>

            <ActivityCardScroller
                loading={isLoading}
                loadFunc={loadMoreActivities}
                items={filteredActivities}
                startDate={startDate}
                endDate={endDate}
            />

            <SnackBarAPIResponse
                messages={actionResponse}
                errors={actionErrors}
                clearResponse={clearErrors}
            />
        </Grid>
    );
};

const mapStateToProps = (state) => ({
    communityId: getActiveCommunityId(state),
    communityName: getActiveCommunityName(state),
    communityImage: getActiveCommunityImage(state),
    isLoading:
        isChallengeLoading(state) ||
        isGroupLoading(state, GROUP_ACTIVE) ||
        isGroupLoading(state, GROUP_UPCOMING),
    isLoadingActive: isGroupLoading(state, GROUP_ACTIVE),
    isLoadingUpcoming: isGroupLoading(state, GROUP_UPCOMING),
    isActionLoading: isChallengesScreenActionLoading(state),
    actionErrors: getChallengesScreenActionErrors(state),
    actionResponse: getChallengesScreenActionResponse(state),
    activeChallenge: getGroupChallenges(state, GROUP_ACTIVE),
    upcomingChallenge: getGroupChallenges(state, GROUP_UPCOMING),
    challengeCoverPhotoUrl: getHeadlinePhotoUrl(state),
    accountPlanData: getCurrentSubscription(state),
    activities: getActivities(state, ACTIVITIES_FEED_TYPE_TRENDING),
    community: getActiveCommunity(state),
    activeCommunityOrgId: getActiveCommunityOrgId(state),
});

const mapDispatchToProps = (dispatch) => ({
    loadFunc: (request) => dispatch(challengesRequested(request)),
    requestActivation: (request) =>
        dispatch(challengeActivationRequested(request)),
    requestDeactivation: (request) =>
        dispatch(challengeDeactivationRequested(request)),
    requestChallengeReport: (challengeId) =>
        dispatch(challengeReportRequested(challengeId)),
    clearErrors: () => dispatch(clearChallengesScreenActionErrors()),
    requestChallenge: ({ challengeId, communityId }) =>
        dispatch(viewChallengeRequested({ challengeId, communityId })),
    fetchCommunityActivities: (communityId) =>
        dispatch(
            activityDataRequested(ACTIVITIES_FEED_TYPE_TRENDING, {
                communityId,
            }),
        ),
});

AdminHome.propTypes = {
    fetchCommunityActivities: PropTypes.func.isRequired,
    activities: PropTypes.shape({
        community: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                actions: PropTypes.arrayOf(
                    PropTypes.shape({
                        actionId: PropTypes.string,
                        actionName: PropTypes.string,
                        imageSizes: PropTypes.arrayOf(
                            PropTypes.shape({
                                url: PropTypes.string,
                            }),
                        ),
                        comments: PropTypes.arrayOf(
                            PropTypes.shape({
                                commentUser: PropTypes.string,
                                commentText: PropTypes.string,
                            }),
                        ),
                        Liked: PropTypes.arrayOf(
                            PropTypes.shape({
                                user: PropTypes.string,
                            }),
                        ),
                        photoSizes: PropTypes.arrayOf(
                            PropTypes.shape({
                                url: PropTypes.string,
                            }),
                        ),
                    }),
                ).isRequired,
                created: PropTypes.string,
                badges: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        name: PropTypes.string,
                    }),
                ),
                masteries: PropTypes.arrayOf(
                    PropTypes.shape({
                        id: PropTypes.string,
                        name: PropTypes.string,
                    }),
                ),
                user: PropTypes.shape({
                    id: PropTypes.string,
                    name: PropTypes.string,
                    imageSizes: PropTypes.arrayOf(
                        PropTypes.shape({
                            url: PropTypes.string,
                        }),
                    ),
                }).isRequired,
            }),
        ).isRequired,
    }).isRequired,
    communityId: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoadingActive: PropTypes.bool.isRequired,
    isLoadingUpcoming: PropTypes.bool.isRequired,
    actionResponse: PropTypes.object,
    loadFunc: PropTypes.func.isRequired,
    activeChallenge: PropTypes.array,
    upcomingChallenge: PropTypes.array,
    requestDeactivation: PropTypes.func.isRequired,
    requestChallengeReport: PropTypes.func.isRequired,
    isActionLoading: PropTypes.bool.isRequired,
    actionErrors: CommonPropTypes.errors,
    clearErrors: PropTypes.func.isRequired,
    challengeCoverPhotoUrl: PropTypes.string,
    requestChallenge: PropTypes.func.isRequired,
    accountPlanData: PropTypes.array,
    communityName: PropTypes.string.isRequired,
    communityImage: PropTypes.string,
    community: PropTypes.object,
    activeCommunityOrgId: PropTypes.string,
};

AdminHome.defaultProps = {
    actionResponse: null,
    loadFunc: noop,
    tableData: null,
    actionErrors: null,
    activeChallenge: null,
    upcomingChallenge: null,
    challengeCoverPhotoUrl: null,
    accountPlanData: [],
    community: null,
    communityImage: null,
    activeCommunityOrgId: '',
    activities: [],
};

const StyledAdminHome = withStyles(styles)(AdminHome);

export default connect(mapStateToProps, mapDispatchToProps)(StyledAdminHome);
